import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Card, CardBody, Table, Input, Badge, Button, Spinner } from "reactstrap";
import Handler from "../../Helpers/Handler";
import { formatIST } from "../../Helpers/Utils";
import AdminHeader from "./Header"; // Import your Admin Header
import { Users, Search, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";

const PAGE_LIMIT = 10;

const AdminCommunityMembers = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Expecting data passed from AdminCommunityList.js
    const { community_id, community_name } = location.state || {};

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const fetchMembers = useCallback(async (p = 1, q = "") => {
        if (!community_id) return;
        setLoading(true);
        const res = await Handler({
            method: "GET",
            url: `/communities/community_members_list.php?community_id=${community_id}&page=${p}&limit=${PAGE_LIMIT}&q=${encodeURIComponent(q)}`,
        });
        setLoading(false);
        if (res.success) {
            setMembers(res.data?.list || []);
            setTotal(res.data?.pagination?.total || 0);
        }
    }, [community_id]);

    useEffect(() => {
        fetchMembers(page, search);
    }, [page, fetchMembers]);

    useEffect(() => {
        const t = setTimeout(() => { setPage(1); fetchMembers(1, search); }, 400);
        return () => clearTimeout(t);
    }, [search, fetchMembers]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

    return (
        <div className="bg-soft-blue min-vh-100 pb-5">
            <AdminHeader />
            <Container py="4" className="mt-4">
                
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                        <div className="icon-box-blue me-3 shadow-sm">
                            <Users size={22} />
                        </div>
                        <div>
                            <h3 className="fw-bold text-navy mb-0">{community_name || "Community"}</h3>
                            <p className="text-muted small mb-0">Managing {total} members</p>
                        </div>
                    </div>
                    <Button color="light" className="border text-navy shadow-sm" onClick={() => navigate(-1)}>
                        <ArrowLeft size={16} className="me-2" /> Back
                    </Button>
                </div>

                {/* Search Bar */}
                <Card className="border-0 shadow-sm mb-4 rounded-4">
                    <CardBody className="py-3">
                        <div className="input-group input-group-sm custom-input-group border rounded-3 overflow-hidden" style={{ maxWidth: "400px" }}>
                            <span className="input-group-text bg-light border-0 text-muted"><Search size={14}/></span>
                            <Input
                                className="bg-light border-0 shadow-none"
                                placeholder="Search members by name or mobile..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </CardBody>
                </Card>

                {/* Members Table */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="table-responsive">
                        <Table hover align="middle" className="mb-0">
                            <thead className="bg-navy text-white">
                                <tr className="small text-uppercase">
                                    <th className="ps-4 py-3 border-0">Member</th>
                                    <th className="border-0">Contact</th>
                                    <th className="border-0">Joined On</th>
                                    <th className="border-0">Role</th>
                                    <th className="border-0 text-end pe-4">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {loading ? (
                                    <tr><td colSpan={5} className="text-center py-5"><Spinner color="primary" /></td></tr>
                                ) : members.length === 0 ? (
                                    <tr><td colSpan={5} className="text-center py-5 text-muted">No members found in this community.</td></tr>
                                ) : (
                                    members.map((m) => (
                                        <tr key={m.user_id} className="border-bottom">
                                            <td className="ps-4">
                                                <div className="d-flex align-items-center">
                                                    <div className="avatar-sm me-3 bg-soft-primary text-primary fw-bold">
                                                        {(m.name || "?").charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="fw-bold text-navy">{m.name}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small">{m.mobile || m.email}</div>
                                            </td>
                                            <td className="small text-muted">{formatIST(m.joined_at)}</td>
                                            <td>
                                                <Badge color="light" className="text-primary border">{m.role}</Badge>
                                            </td>
                                            <td className="pe-4 text-end">
                                                <Badge color="success" pill className="px-2">Active Member</Badge>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card>

                {/* Pagination */}
                {total > PAGE_LIMIT && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <span className="text-muted small">Showing {members.length} of {total}</span>
                        <div className="d-flex gap-2">
                            <Button color="outline-primary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1}>
                                <ChevronLeft size={16} />
                            </Button>
                            <Button color="primary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
                                <ChevronRight size={16} />
                            </Button>
                        </div>
                    </div>
                )}
            </Container>

            <style>{`
                .avatar-sm { 
                    width: 32px; height: 32px; border-radius: 8px; 
                    display: flex; align-items: center; justify-content: center; font-size: 0.8rem;
                }
                .bg-soft-primary { background-color: #e0e7ff; color: #4338ca; }
                .text-navy { color: #1e3a8a; }
                .bg-navy { background-color: #1e3a8a; }
                .icon-box-blue { background: #1e3a8a; color: white; padding: 10px; border-radius: 12px; }
                .rounded-4 { border-radius: 1rem !important; }
            `}</style>
        </div>
    );
};

export default AdminCommunityMembers;